import axios from "axios";
import React, { useEffect, useState } from "react";
import {
  Container,
  Table,
  Spinner,
  Row,
  Col,
  Form,
  Button,
  Modal,
  InputGroup,
  Alert,
  Pagination,
  Dropdown,
  Card,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { Search, Eye, EyeOff, Plus, X } from "lucide-react";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";
import "./UserMng.css";
import { getActionPermissions } from "../../Utils/ActionPermissionChecker";
import { showMyAlert } from "../../Utils/alertService";
import { doAuditLog } from "../../Utils/saveHistoryAction";

const UserManagement = () => {
  const navigate = useNavigate();
  const [currentUserRole, setCurrentUserRole] = useState("");
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [startDateFilter, setStartDateFilter] = useState("");
  const [endDateFilter, setEndDateFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [locations, setLocations] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [locationFilter, setLocationFilter] = useState("");
  const [shiftFilter, setShiftFilter] = useState("");
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    mobile_number: "",
    // password: "",
    // password_confirmation: "",
    employee_id: "",
    role: "employee",
    approval_status: "true",
  });
  const companyKey = sessionStorage.getItem("companyKey");

  const [showEditModal, setShowEditModal] = useState(false);
  const [editData, setEditData] = useState({
    id: "",
    full_name: "",
    email: "",
    mobile_number: "",
    employee_id: "",
    approval_status: "",
  });

  const [showFilters, setShowFilters] = useState(false)

  const userRole = sessionStorage.getItem("role");
  const { canView, canAdd, canEdit, canDelete } =
    getActionPermissions(userRole);

  useEffect(() => {
    const checkAuth = () => {
      const userId = sessionStorage.getItem("userMobile");
      if (!userId) {
        navigate("/signIn");
      }
    };

    checkAuth();
  }, [navigate]);

  useEffect(() => {
    const fetchCurrentUserRole = async () => {
      try {
        const userMobile =
          localStorage.getItem("userMobile") ||
          sessionStorage.getItem("userMobile");
        if (!userMobile) {
          navigate("/signIn");
          return;
        }

        const response = await fetch(
          "https://api.dremerz.net/api/DZ_8922_Payroll_PayrollCreateAdmin_PayrollAdminList"
        );
        if (!response.ok) {
          throw new Error("Failed to fetch admin data");
        }

        const data = await response.json();
        const adminList = Array.isArray(data) ? data : [data];
        const currentUser = adminList.find(
          (admin) => admin.mobileNumber === userMobile
        );
        if (currentUser) {
          setCurrentUserRole(currentUser.role);
          sessionStorage.setItem("role", currentUser.role);
        } else {
          setCurrentUserRole("employee");
          sessionStorage.setItem("role", "employee");
        }
      } catch (error) {
        console.error("Error fetching admin role:", error);
        setCurrentUserRole("employee");
        sessionStorage.setItem("role", "employee");
      }
    };

    fetchCurrentUserRole();
  }, [navigate]);
  const isSuperAdmin = currentUserRole === "superadmin";

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        "https://api.dremerz.net/api/DZ_8922_Payroll_PayrollRegistration_PayrollRegistrationUsers"
      );
      const data = response.data;
      const filtered = data.reverse().filter((d) => d.companyKey === companyKey);
      console.log(filtered, "Filtered Data");

      setUsers(filtered);
      setFilteredUsers(data);
      const uniqueLocations = [
        ...new Set(data.map((user) => user.locationName)),
      ].filter(Boolean);
      setLocations(uniqueLocations);

      const allShifts = [];
      data.forEach((user) => {
        if (user.shift) {
          try {
            const parsedShifts = JSON.parse(user.shift);
            if (Array.isArray(parsedShifts)) {
              parsedShifts.forEach((shift) => {
                if (
                  shift.shiftName &&
                  !allShifts.some((s) => s.shiftName === shift.shiftName)
                ) {
                  allShifts.push(shift);
                }
              });
            }
          } catch (e) {
            console.error("Error parsing shift data:", e);
          }
        }
      });
      setShifts(allShifts);

      setCurrentPage(1);
    } catch (error) {
      console.error("Error fetching users:", error);
      setError("Failed to load users. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const filterUsers = () => {
    let filtered = [...users];

    if (locationFilter) {
      filtered = filtered.filter(
        (user) => user.locationName && user.locationName === locationFilter
      );
    }

    if (shiftFilter) {
      filtered = filtered.filter((user) => {
        if (!user.shift) return false;
        try {
          const userShifts = JSON.parse(user.shift);
          return (
            Array.isArray(userShifts) &&
            userShifts.some((shift) => shift.shiftName === shiftFilter)
          );
        } catch (e) {
          return false;
        }
      });
    }

    if (startDateFilter || endDateFilter) {
      filtered = filtered.filter((user) => {
        if (!user.approval_date) return false;
        const [day, month, year] = user.approval_date.split("-");
        const userDate = new Date(`${year}-${month}-${day}`);

        let includeUser = true;

        if (startDateFilter) {
          const [startYear, startMonth, startDay] = startDateFilter.split("-");
          const startDate = new Date(`${startYear}-${startMonth}-${startDay}`);
          includeUser = includeUser && userDate >= startDate;
        }

        if (endDateFilter) {
          const [endYear, endMonth, endDay] = endDateFilter.split("-");
          const endDate = new Date(`${endYear}-${endMonth}-${endDay}`);
          includeUser = includeUser && userDate <= endDate;
        }

        return includeUser;
      });
    }

    if (searchQuery.trim() !== "") {
      filtered = filtered.filter((user) => {
        const searchableFields = [
          user.full_name || "",
          user.email || "",
          user.mobile_number || "",
          user.employee_id || "",
          user.role || "",
          user.id?.toString() || "",
          user.approval_status === true ||
            user.approval_status === "true" ||
            user.approval_status === "1"
            ? "approved"
            : "pending",
          user.locationName || "",
          user.shift
            ? JSON.parse(user.shift)
              .map((s) => s.shiftName)
              .join(" ")
            : "" || user.approval_date || "",
        ];

        return searchableFields.some((field) =>
          field.toLowerCase().includes(searchQuery.toLowerCase())
        );
      });
    }

    setFilteredUsers(filtered);
    setCurrentPage(1);
  };

  useEffect(() => {
    filterUsers();
  }, [
    users,
    searchQuery,
    locationFilter,
    shiftFilter,
    startDateFilter,
    endDateFilter,
  ]);

  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleDeleteClick = (user) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const handleEditClick = (user) => {
    setEditData({
      id: user.id,
      full_name: user.full_name || "",
      email: user.email || "",
      mobile_number: user.mobile_number || "",
      employee_id: user.employee_id || "",
      approval_status:
        user.approval_status === true ||
          user.approval_status === "true" ||
          user.approval_status === 1
          ? "true"
          : "false",
    });
    setShowEditModal(true);
  };

  const handleEditChange = (field, value) => {
    setEditData({ ...editData, [field]: value });
  };

  const handleDeleteConfirm = async () => {
    try {
      const oldData = { ...selectedUser };
      const newData = { ...selectedUser, status: "deleted" };

      await axios.delete(
        `https://api.dremerz.net/api/DZ_8922_Payroll_PayrollRegistration_PayrollRegistrationUsers/${selectedUser.id}`
      );

      doAuditLog("Delete", "Add Location", oldData, "");

      const updatedUsers = users.filter((user) => user.id !== selectedUser.id);
      setUsers(updatedUsers);
      setShowDeleteModal(false);
      showMyAlert({
        type: "success", // "success" | "danger" | "warning" | "info"
        title: "Deleted",
        message: `User "${selectedUser.full_name}" deleted successfully!`,
        duration: 2500, // auto-close in 2.5s
      });
    } catch (error) {
      console.error("Error deleting user:", error);
      setError("Failed to delete user. Please try again.");
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();

    try {
      setIsLoading(true);

      const payload = {
        ...editData,
        approval_status: editData.approval_status === "true" ? "true" : "false",
      };

      const response = await fetch(
        "https://api.dremerz.net/api/DZ_8922_Payroll_PayrollRegistration_PayrollRegistrationUsers"
      );
      const allUsersRes = await response.json();
      const allUsers = allUsersRes.filter((u) => u.companyKey === companyKey);

      const conflicts = allUsers.filter(
        (u) =>
          u.id !== editData.id &&
          (u.mobile_number === editData.mobile_number ||
            u.email === editData.email ||
            u.employee_id === editData.employee_id)
      );

      if (conflicts.length > 0) {
        const duplicateMessages = [];

        if (conflicts.some((u) => u.mobile_number === editData.mobile_number))
          duplicateMessages.push(`Mobile Number: ${editData.mobile_number}`);
        if (conflicts.some((u) => u.email === editData.email))
          duplicateMessages.push(`Email: ${editData.email}`);
        if (conflicts.some((u) => u.employee_id === editData.employee_id))
          duplicateMessages.push(`Employee ID: ${editData.employee_id}`);

        showMyAlert({
          type: "warning", // "success" | "danger" | "warning" | "info"
          title: "Warning",
          message: `Already registered with:\n• ${duplicateMessages.join(
            "\n• "
          )}\n\nPlease check and try again.`,
          duration: 2500, // auto-close in 2.5s
        });

        setIsLoading(false);
        return;
      }

      const oldData = users.find((u) => u.id === editData.id);

      const updateResponse = await fetch(
        `https://api.dremerz.net/api/DZ_8922_Payroll_PayrollRegistration_PayrollRegistrationUsers/${editData.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      if (!updateResponse.ok) throw new Error("Failed to update user.");

      setUsers((prev) =>
        prev.map((user) =>
          user.id === editData.id ? { ...user, ...payload } : user
        )
      );

      setShowEditModal(false);
      showMyAlert({
        type: "success", // "success" | "danger" | "warning" | "info"
        title: "Success",
        message: "User details updated successfully!",
        duration: 2500, // auto-close in 2.5s
      });
      doAuditLog("Update", "Add Employee", oldData, payload);
    } catch (error) {
      console.error("Error updating user:", error);
      setError("Failed to update user details.");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAndProcessPayslipLabels = async () => {
    try {
      const response = await axios.get(
        "https://api.dremerz.net/api/DZ_8922_Payroll_PayrollPayslip_PayrollPayslipData_PayrollPayslipLabelsList"
      );

      const processSection = (sectionName) => {
        return response
          .filter((item) => item.sectionName === sectionName)
          .map((item) => ({
            [item.value]: "true",
          }));
      };

      const earnings = processSection("Earnings");
      const deductions = processSection("Deductions");

      return {
        earnings: JSON.stringify(earnings),
        deductions: JSON.stringify(deductions),
      };
    } catch (error) {
      console.error("Error fetching payslip labels:", error);
      return {
        earnings: JSON.stringify([]),
        deductions: JSON.stringify([]),
      };
    }
  };

  const clearError = () => {
    setError(null);
  };

  const handleChange = (name, value) => {
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const storeUserData = async (userData) => {
    try {
      console.log("User data stored successfully", userData);
    } catch (error) {
      console.error("Error storing user data:", error);
    }
  };

  const checkUserExists = async (mobile, employeeId, email) => {
    try {
      const response = await fetch(
        "https://api.dremerz.net/api/DZ_8922_Payroll_PayrollRegistration_PayrollRegistrationUsers"
      );
      const data = await response.json();

      if (response.ok) {
        const mobileExists = data.some((user) => user.mobile_number === mobile);
        const employeeIdExists = data.some(
          (user) => user.employee_id === employeeId
        );
        const employeeMailExists = data.some((user) => user.email === email);

        return {
          mobileExists,
          employeeIdExists,
          employeeMailExists,
          anyExists: mobileExists || employeeIdExists || employeeMailExists,
        };
      }
      return {
        mobileExists: false,
        employeeIdExists: false,
        employeeMailExists: false,
        anyExists: false,
      };
    } catch (error) {
      console.error("Error checking user data:", error);
      return {
        mobileExists: false,
        employeeIdExists: false,
        employeeMailExists: false,
        anyExists: false,
      };
    }
  };

  const handleRegistrationSubmit = async (e) => {
    e.preventDefault();

    const {
      full_name,
      email,
      mobile_number,
      // password,
      // password_confirmation,
      employee_id,
    } = formData;

    if (
      !full_name ||
      !email ||
      !mobile_number ||
      // !password ||
      // !password_confirmation ||
      !employee_id
    ) {
      showMyAlert({
        type: "warning", // "success" | "danger" | "warning" | "info"
        title: "Warning",
        message: "All fields are required.",
        duration: 2500, // auto-close in 2.5s
      });
      return;
    }

    // if (password !== password_confirmation) {
    //   alert("Passwords do not match.");
    //   return;
    // }

    if (mobile_number.length < 10) {
      showMyAlert({
        type: "warning", // "success" | "danger" | "warning" | "info"
        title: "Warning",
        message: "Please enter a valid mobile number.",
        duration: 2500, // auto-close in 2.5s
      });
      return;
    }

    // if (password.length < 6) {
    //   alert("Password must be at least 6 characters long.");
    //   return;
    // }

    setIsLoading(true);

    try {
      const userCheck = await checkUserExists(
        mobile_number,
        employee_id,
        email
      );

      if (
        userCheck.mobileExists &&
        userCheck.employeeIdExists &&
        userCheck.employeeMailExists
      ) {
        showMyAlert({
          type: "warning", // "success" | "danger" | "warning" | "info"
          title: "Already Registered",
          message: "Mobile number, employee ID, and email are already registered.",
          duration: 2500, // auto-close in 2.5s
        });
        return;
      } else if (userCheck.mobileExists) {
        showMyAlert({
          type: "warning", // "success" | "danger" | "warning" | "info"
          title: "Already Registered",
          message: "This mobile number is already registered.",
          duration: 2500, // auto-close in 2.5s
        });
        return;
      } else if (userCheck.employeeIdExists) {
        showMyAlert({
          type: "warning", // "success" | "danger" | "warning" | "info"
          title: "Already Registered",
          message: "This employee ID is already registered.",
          duration: 2500, // auto-close in 2.5s
        });
        return;
      } else if (userCheck.employeeMailExists) {
        showMyAlert({
          type: "warning", // "success" | "danger" | "warning" | "info"
          title: "Already Registered",
          message: "This employee email is already registered.",
          duration: 2500, // auto-close in 2.5s
        });
        return;
      }

      const getCurrentDate = () => {
        const today = new Date();
        const day = String(today.getDate()).padStart(2, "0");
        const month = String(today.getMonth() + 1).padStart(2, "0");
        const year = today.getFullYear();
        return `${day}-${month}-${year}`;
      };

      const payslipData = await fetchAndProcessPayslipLabels();

      const registrationData = {
        full_name,
        email,
        mobile_number,
        // password,
        employee_id,
        role: "employee",
        approval_status: "true",
        approval_date: getCurrentDate(),
        earnings: payslipData.earnings,
        deductions: payslipData.deductions,
        companyKey: companyKey,
      };

      const response = await fetch(
        "https://api.dremerz.net/api/DZ_8922_Payroll_PayrollRegistration_PayrollRegistrationUsers",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(registrationData),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Registration failed");
      }

      doAuditLog("Add", "Add Employee", "", registrationData);

      showMyAlert({
        type: "success", // "success" | "danger" | "warning" | "info"
        title: "Added Successfully",
        message: "Employee Added Successfully",
        duration: 2500, // auto-close in 2.5s
      });

      setFormData({
        full_name: "",
        email: "",
        mobile_number: "",
        // password: "",
        // password_confirmation: "",
        employee_id: "",
        role: "employee",
        approval_status: "true",
      });

      setShowRegistrationModal(false);
      fetchUsers();
    } catch (error) {
      console.error("Registration error:", error);
      showMyAlert({
        type: "warning", // "success" | "danger" | "warning" | "info"
        title: "Warning",
        message: error.message || "An error occurred during registration",
        duration: 2500, // auto-close in 2.5s
      });
    } finally {
      setIsLoading(false);
    }
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredUsers
    .slice(indexOfFirstItem, indexOfLastItem)
    .reverse();
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const handleItemsPerPageChange = (count) => {
    setItemsPerPage(count);
    setCurrentPage(1);
  };

  const handleDownloadExcel = () => {
    const excelData = currentItems.map((user) => ({
      "Full Name": user.full_name,
      Email: user.email,
      mobile: user.mobile_number,
      "Employee ID": user.employee_id,
    }));

    const worksheet = XLSX.utils.json_to_sheet(excelData);

    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "Employees");

    XLSX.writeFile(workbook, "Employee_List.xlsx");
  };

  const handleDownloadPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text("Employee List", 14, 16);
    const tableColumn = ["Full Name", "Email", "Mobile Number", "Employee ID"];
    const tableRows = currentItems.map((user) => [
      user.full_name,
      user.email || "N/A",
      user.mobile_number,
      user.employee_id || "N/A",
    ]);
    doc.autoTable({
      head: [tableColumn],
      body: tableRows,
      startY: 22,
      styles: { fontSize: 10, cellPadding: 2 },
      headStyles: { fillColor: [41, 128, 185], textColor: 255 },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      margin: { left: 14, right: 14 },
    });
    doc.save("Employee_List.pdf");
  };

  const isMobile = window.innerWidth < 768;

  return (
    <Container className="mt-3">
      {/* Header Section */}
      <div className="row align-items-center mb-2 border rounded bg-light py-2 mx-1">
        <div className="col-12 col-md-6 mb-2 mb-md-0 d-flex justify-content-center justify-content-md-start">
          <h5 className="mb-0 d-flex align-items-center gap-2">
            Add New Employee
          </h5>
        </div>
        <div className="col-12 col-md-6 d-flex justify-content-center justify-content-md-end">
          <div className="d-flex align-items-center justify-content-end gap-2">
            <Button
              variant="outline-success"
              size="sm"
              title="Download Excel"
              onClick={handleDownloadExcel}
            >
              Export Excel
            </Button>
            <Button
              variant="outline-danger"
              size="sm"
              title="Download PDF"
              onClick={handleDownloadPDF}
            >
              Export PDF
            </Button>

            {canAdd && (
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => setShowRegistrationModal(true)}
                className="d-flex align-items-center gap-2"
              >
                Add Employee
              </Button>
            )}
          </div>
        </div>
      </div>

      {isMobile && (
        <div className="d-flex justify-content-end mb-2">
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
          >
            {showFilters ? "Hide Filters" : "Show Filters"}
          </Button>
        </div>
      )}

      {/* Search Section */}
      <div className={`row align-items-center mb-1 border rounded bg-light py-2 mx-1 ${(showFilters ? "d-flex" : "d-none")}`}>
        <div className="col-12 col-md-5">
          <Form.Label className="fw-semibold small mb-1 ms-1">
            Search
          </Form.Label>
          <div className="input-group input-group-sm position-relative">
            <span className="input-group-text bg-light border-end-0">
              <Search size={16} className="text-muted" />
            </span>
            <input
              type="text"
              className="form-control border-start-0 pe-5"
              placeholder="Search here ..."
              value={searchQuery}
              onChange={handleSearch}
            />
          </div>
        </div>

        <div className="col-12 col-md-4 mt-2 mt-md-0">
          <div className="row g-2">
            <div className="col-6">
              <Form.Label className="fw-semibold small mb-1 ms-1">
                Start Date
              </Form.Label>
              <Form.Control
                type="date"
                size="sm"
                value={startDateFilter}
                onChange={(e) => setStartDateFilter(e.target.value)}
                placeholder="Start Date"
              />
            </div>
            <div className="col-6">
              <Form.Label className="fw-semibold small mb-1 ms-1">
                End Date
              </Form.Label>
              <Form.Control
                type="date"
                size="sm"
                value={endDateFilter}
                onChange={(e) => setEndDateFilter(e.target.value)}
                placeholder="End Date"
                min={startDateFilter}
              />
            </div>
          </div>
        </div>

        {(startDateFilter || endDateFilter || searchQuery) && (
          <div className="col-12 col-md-2 mt-2 mt-md-0">
            <Form.Label className="fw-semibold small mb-1 ms-1 text-light">
              Clear
            </Form.Label>
            <Button
              variant="outline-danger"
              size="sm"
              className="form-control"
              onClick={() => {
                setStartDateFilter("");
                setEndDateFilter("");
                setSearchQuery("");
              }}
            >
              Clear
            </Button>
          </div>
        )}
      </div>

      {totalPages > 0 && (
        <div className="row align-items-center justify-content-between mb-2 p-2">
          <div className="col-5 col-md-auto">
            <div className="d-flex align-items-center gap-2">
              <span className="text-muted small">Paginate</span>
              <Dropdown>
                <Dropdown.Toggle
                  size="sm"
                  variant="outline-secondary"
                  id="dropdown-items-per-page"
                >
                  {itemsPerPage}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {[5, 10, 20, 50, 100].map((count) => (
                    <Dropdown.Item
                      key={count}
                      onClick={() => handleItemsPerPageChange(count)}
                      active={itemsPerPage === count}
                    >
                      {count}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>

          {/* Pagination controls */}
          <div className="col-7 col-md-auto">
            <Pagination
              size="sm"
              className="mb-0 justify-content-center justify-content-md-end"
            >
              <Pagination.First
                onClick={() => paginate(1)}
                disabled={currentPage === 1}
              />
              <Pagination.Prev
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
              />

              {isMobile ? (
                <>
                  <Pagination.Item active>{currentPage}</Pagination.Item>
                  {totalPages > 1 && (
                    <>
                      <Pagination.Ellipsis disabled />
                      <Pagination.Item onClick={() => paginate(totalPages)}>
                        {totalPages}
                      </Pagination.Item>
                    </>
                  )}
                </>
              ) : (
                (() => {
                  const items = [];
                  const maxVisiblePages = 5;
                  let startPage = Math.max(
                    1,
                    currentPage - Math.floor(maxVisiblePages / 2)
                  );
                  let endPage = Math.min(
                    totalPages,
                    startPage + maxVisiblePages - 1
                  );

                  if (endPage - startPage + 1 < maxVisiblePages) {
                    startPage = Math.max(1, endPage - maxVisiblePages + 1);
                  }

                  if (startPage > 1) {
                    items.push(<Pagination.Ellipsis key="start-ellipsis" />);
                  }

                  for (let number = startPage; number <= endPage; number++) {
                    items.push(
                      <Pagination.Item
                        key={number}
                        active={number === currentPage}
                        onClick={() => paginate(number)}
                      >
                        {number}
                      </Pagination.Item>
                    );
                  }

                  if (endPage < totalPages) {
                    items.push(<Pagination.Ellipsis key="end-ellipsis" />);
                  }

                  return items;
                })()
              )}

              <Pagination.Next
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
              />
              <Pagination.Last
                onClick={() => paginate(totalPages)}
                disabled={currentPage === totalPages}
              />
            </Pagination>
          </div>
        </div>
      )}

      {error && (
        <Alert
          variant="danger"
          onClose={clearError}
          dismissible
          className="mb-3"
        >
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert variant="success" className="mb-3">
          {successMessage}
        </Alert>
      )}

      <div className="table-responsive">
        {isMobile ? (
          currentItems.length > 0 ? (
            currentItems.map((user, index) => (
              <Card
                key={user.id}
                className="mb-3 shadow-sm"
                style={{ textAlign: "left", borderRadius: "12px" }}
              >
                <Card.Body>
                  <Table
                    striped
                    bordered
                    responsive
                    hover
                    size="sm"
                    className="mb-2"
                  >
                    <tbody>
                      <tr>
                        <td className="fw-bold">S.No</td>
                        <td>
                          {filteredUsers.length - (indexOfFirstItem + index)}
                        </td>
                      </tr>
                      <tr>
                        <td className="fw-bold">Full Name</td>
                        <td>{user.full_name}</td>
                      </tr>
                      <tr>
                        <td className="fw-bold">Email ID</td>
                        <td>{user.email || "N/A"}</td>
                      </tr>
                      <tr>
                        <td className="fw-bold" style={{ minWidth: 150 }}>
                          Mobile Number
                        </td>
                        <td>{user.mobile_number}</td>
                      </tr>
                      <tr>
                        <td className="fw-bold">Employee ID</td>
                        <td>{user.employee_id || "N/A"}</td>
                      </tr>
                      <tr>
                        <td className="fw-bold">Created At</td>
                        <td>{user.approval_date || "N/A"}</td>
                      </tr>
                    </tbody>
                  </Table>
                  {(canDelete || canEdit) && (
                    <div className="d-flex justify-content-end gap-2 mt-2">
                      <td className="d-flex gap-2">
                        {canEdit && (
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() => handleEditClick(user)}
                            disabled={!isSuperAdmin}
                          >
                            Edit
                          </Button>
                        )}
                        {canDelete && (
                          <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() => handleDeleteClick(user)}
                            disabled={!isSuperAdmin}
                          >
                            Delete
                          </Button>
                        )}
                      </td>
                    </div>
                  )}
                </Card.Body>
              </Card>
            ))
          ) : (
            <div className="text-center py-4">
              {searchQuery || locationFilter || shiftFilter
                ? "No users found matching your search criteria."
                : "No users found."}
            </div>
          )
        ) : (
          <Table striped bordered hover responsive className="mb-0">
            <thead className="">
              <tr>
                <th id="text-white-important">S.No</th>
                <th id="text-white-important">Full Name</th>
                <th id="text-white-important">Email ID</th>
                <th id="text-white-important">Mobile Number</th>
                <th id="text-white-important">Employee ID</th>
                <th id="text-white-important">Created At</th>
                {(canEdit || canDelete) && (
                  <th id="text-white-important">Actions</th>
                )}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" className="text-center py-4">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-2 mb-0">Loading users...</p>
                  </td>
                </tr>
              ) : currentItems.length > 0 ? (
                currentItems.toReversed().map((user, index) => (
                  <tr key={user.id} className="text-start">
                    <td>{filteredUsers.length - (indexOfFirstItem + index)}</td>
                    <td>{user.full_name}</td>
                    <td>{user.email || "N/A"}</td>
                    <td>{user.mobile_number}</td>
                    <td>{user.employee_id || "N/A"}</td>
                    <td>{user.approval_date || "N/A"}</td>
                    {(canDelete || canEdit) && (
                      <td classNames="d-flex gap-2">
                        <td className="d-flex gap-2">
                          {canEdit && (
                            <Button
                              size="sm"
                              variant="outline-primary"
                              onClick={() => handleEditClick(user)}
                            >
                              Edit
                            </Button>
                          )}
                          {canDelete && (
                            <Button
                              size="sm"
                              variant="outline-danger"
                              onClick={() => handleDeleteClick(user)}
                            >
                              Delete
                            </Button>
                          )}
                        </td>
                      </td>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="text-center py-4">
                    {searchQuery || locationFilter || shiftFilter
                      ? "No users found matching your search criteria."
                      : "No users found."}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        )}
      </div>

      <div className="col-12 my-2 text-end">
        <span className="text-muted ">
          Showing {indexOfFirstItem + 1} to{" "}
          {Math.min(indexOfLastItem, filteredUsers.length)} of{" "}
          {filteredUsers.length} entries
        </span>
      </div>

      <Modal
        show={showRegistrationModal}
        onHide={() => {
          setFormData({
            full_name: "",
            email: "",
            mobile_number: "",
            // password: "",
            // password_confirmation: "",
            employee_id: "",
            role: "employee",
            approval_status: "true",
          });
          setShowRegistrationModal(false);
        }}
        centered
        size="lg"
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>Add New Employee</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Container>
            <Form onSubmit={handleRegistrationSubmit}>
              <Row className="mb-3">
                <Col md={6} className="mb-3 mb-md-0">
                  <Form.Group>
                    <Form.Label
                      className="fw-semibold"
                      style={{
                        color: "#374151",
                        fontSize: "14px",
                        letterSpacing: "0.025em",
                      }}
                    >
                      Full Name <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter full name"
                      value={formData.full_name}
                      onChange={(e) =>
                        handleChange("full_name", e.target.value)
                      }
                      required
                      className="py-2"
                      style={{
                        backgroundColor: "rgba(248, 250, 252, 0.8)",
                        border: "1.5px solid #e2e8f0",
                        borderRadius: "8px",
                        fontSize: "14px",
                        transition: "all 0.2s ease",
                      }}
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label
                      className="fw-semibold"
                      style={{
                        color: "#374151",
                        fontSize: "14px",
                        letterSpacing: "0.025em",
                      }}
                    >
                      Email <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="email"
                      placeholder="Enter email"
                      value={formData.email}
                      onChange={(e) => handleChange("email", e.target.value)}
                      required
                      className="py-2"
                      style={{
                        backgroundColor: "rgba(248, 250, 252, 0.8)",
                        border: "1.5px solid #e2e8f0",
                        borderRadius: "8px",
                        fontSize: "14px",
                        transition: "all 0.2s ease",
                      }}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6} className="mb-3 mb-md-0">
                  <Form.Group>
                    <Form.Label
                      className="fw-semibold"
                      style={{
                        color: "#374151",
                        fontSize: "14px",
                        letterSpacing: "0.025em",
                      }}
                    >
                      Mobile Number <span className="text-danger">*</span>
                    </Form.Label>
                    <InputGroup>
                      <InputGroup.Text
                        style={{
                          backgroundColor: "rgba(241, 245, 249, 0.8)",
                          border: "1.5px solid #e2e8f0",
                          borderRight: "none",
                          color: "#4a5568",
                          fontSize: "14px",
                          fontWeight: "500",
                          borderRadius: "8px 0 0 8px",
                        }}
                      >
                        +91
                      </InputGroup.Text>
                      <Form.Control
                        type="tel"
                        placeholder="9876543210"
                        value={formData.mobile_number}
                        onChange={(e) =>
                          handleChange("mobile_number", e.target.value)
                        }
                        maxLength={10}
                        pattern="[0-9]{10}"
                        required
                        className="py-2"
                        style={{
                          backgroundColor: "rgba(248, 250, 252, 0.8)",
                          border: "1.5px solid #e2e8f0",
                          borderLeft: "none",
                          fontSize: "14px",
                          transition: "all 0.2s ease",
                          borderRadius: "0 8px 8px 0",
                        }}
                      />
                    </InputGroup>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label
                      className="fw-semibold"
                      style={{
                        color: "#374151",
                        fontSize: "14px",
                        letterSpacing: "0.025em",
                      }}
                    >
                      Employee ID <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="EMP12345"
                      value={formData.employee_id}
                      onChange={(e) =>
                        handleChange("employee_id", e.target.value)
                      }
                      required
                      className="py-2"
                      style={{
                        backgroundColor: "rgba(248, 250, 252, 0.8)",
                        border: "1.5px solid #e2e8f0",
                        borderRadius: "8px",
                        fontSize: "14px",
                        textTransform: "uppercase",
                        transition: "all 0.2s ease",
                      }}
                    />
                  </Form.Group>
                </Col>
              </Row>

              {/* <Row className="mb-4">
                <Col md={6} className="mb-3 mb-md-0">
                  <Form.Group>
                    <Form.Label
                      className="fw-semibold"
                      style={{
                        color: "#374151",
                        fontSize: "14px",
                        letterSpacing: "0.025em",
                      }}
                    >
                      Password <span className="text-danger">*</span>
                    </Form.Label>
                    <InputGroup>
                      <Form.Control
                        type={showPassword ? "text" : "password"}
                        placeholder="At least 6 characters"
                        value={formData.password}
                        onChange={(e) =>
                          handleChange("password", e.target.value)
                        }
                        minLength={6}
                        required
                        className="py-2"
                        style={{
                          backgroundColor: "rgba(248, 250, 252, 0.8)",
                          border: "1.5px solid #e2e8f0",
                          borderRight: "none",
                          fontSize: "14px",
                          transition: "all 0.2s ease",
                          borderRadius: "8px 0 0 8px",
                        }}
                      />
                      <Button
                        variant="outline-secondary"
                        onClick={() => setShowPassword(!showPassword)}
                        style={{
                          backgroundColor: "rgba(248, 250, 252, 0.8)",
                          border: "1.5px solid #e2e8f0",
                          borderLeft: "none",
                          color: "#6b7280",
                          borderRadius: "0 8px 8px 0",
                        }}
                      >
                        {showPassword ? (
                          <EyeOff size={16} />
                        ) : (
                          <Eye size={16} />
                        )}
                      </Button>
                    </InputGroup>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label
                      className="fw-semibold"
                      style={{
                        color: "#374151",
                        fontSize: "14px",
                        letterSpacing: "0.025em",
                      }}
                    >
                      Confirm Password <span className="text-danger">*</span>
                    </Form.Label>
                    <InputGroup>
                      <Form.Control
                        type={showConfirmPassword ? "text" : "password"}
                        placeholder="Confirm password"
                        value={formData.password_confirmation}
                        onChange={(e) =>
                          handleChange("password_confirmation", e.target.value)
                        }
                        minLength={6}
                        required
                        className="py-2"
                        style={{
                          backgroundColor: "rgba(248, 250, 252, 0.8)",
                          border: "1.5px solid #e2e8f0",
                          borderRight: "none",
                          fontSize: "14px",
                          transition: "all 0.2s ease",
                          borderRadius: "8px 0 0 8px",
                        }}
                      />
                      <Button
                        variant="outline-secondary"
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                        style={{
                          backgroundColor: "rgba(248, 250, 252, 0.8)",
                          border: "1.5px solid #e2e8f0",
                          borderLeft: "none",
                          color: "#6b7280",
                          borderRadius: "0 8px 8px 0",
                        }}
                      >
                        {showConfirmPassword ? (
                          <EyeOff size={16} />
                        ) : (
                          <Eye size={16} />
                        )}
                      </Button>
                    </InputGroup>
                  </Form.Group>
                </Col>
              </Row> */}
            </Form>
          </Container>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setFormData({
                full_name: "",
                email: "",
                mobile_number: "",
                // password: "",
                // password_confirmation: "",
                employee_id: "",
                role: "employee",
                approval_status: "true",
              });
              setShowRegistrationModal(false);
            }}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleRegistrationSubmit}
            disabled={isLoading}
            className="d-flex align-items-center gap-2"
          >
            {isLoading ? (
              <>
                <Spinner
                  animation="border"
                  size="sm"
                  style={{ width: "16px", height: "16px" }}
                />
                Processing...
              </>
            ) : (
              <>
                <Plus size={16} />
                Add Employee
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete the following user?</p>
          <ul className="list-unstyled">
            <li>
              <strong>Name:</strong> {selectedUser?.full_name}
            </li>
            <li>
              <strong>Mobile:</strong> {selectedUser?.mobile_number}
            </li>
            <li>
              <strong>Role:</strong> {selectedUser?.role}
            </li>
          </ul>
          <p className="text-danger">This action cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteConfirm}>
            Confirm Delete
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Employee Modal */}
      <Modal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        centered
        size="lg"
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit Employee Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Container>
            <Form onSubmit={handleEditSubmit}>
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Full Name</Form.Label>
                    <Form.Control
                      type="text"
                      value={editData.full_name}
                      onChange={(e) =>
                        handleEditChange("full_name", e.target.value)
                      }
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Email</Form.Label>
                    <Form.Control
                      type="email"
                      value={editData.email}
                      onChange={(e) =>
                        handleEditChange("email", e.target.value)
                      }
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Mobile Number</Form.Label>
                    <Form.Control
                      type="text"
                      maxLength={10}
                      value={editData.mobile_number}
                      onChange={(e) =>
                        handleEditChange("mobile_number", e.target.value)
                      }
                      required
                      readOnly
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Employee ID</Form.Label>
                    <Form.Control
                      type="text"
                      value={editData.employee_id}
                      onChange={(e) =>
                        handleEditChange("employee_id", e.target.value)
                      }
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Approval Status</Form.Label>
                    <Form.Select
                      value={editData.approval_status}
                      onChange={(e) =>
                        handleEditChange("approval_status", e.target.value)
                      }
                      required
                    >
                      <option value="true">Approved</option>
                      <option value="false">Pending</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          </Container>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowEditModal(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleEditSubmit}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Spinner animation="border" size="sm" /> Updating...
              </>
            ) : (
              "Save Changes"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default UserManagement;
